// src/pages/EPServiceManagement.jsx
import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Typography,
  useMediaQuery,
  Chip,
  Button,
  IconButton,
  Card,
  CircularProgress,
  TextField, // 👈 eklendi
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DynamicTable from 'src/components/shared/DynamicTable';
import Services from 'src/services/Services';
import MallPriceListDialog from 'src/modals/MallPriceListModal';
import MallPriceEditDialog from 'src/modals/MallPriceEditDialog';
import AddEntityButton from 'src/components/shared/AddEntityButton';
import CouponCreateDialog from 'src/modals/CouponCreateDialog';

export default function EPServiceManagement() {
  const parsedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isMobile = useMediaQuery('(max-width:768px)');

  const [malls, setMalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState(''); // 👈 arama state

  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedMallName, setSelectedMallName] = useState('');
  const [selectedMallID, setSelectedMallID] = useState(null);
  const [openCoupon, setOpenCoupon] = useState(false);

  const fetchMalls = async () => {
    try {
      setLoading(true);
      const res = await Services.GetMalls(parsedUser?.token);
      setMalls(res?.data?.result || []);
    } catch (err) {
      console.error('Error fetching malls', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMalls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(
    () =>
      malls.map((m) => ({
        id: m.id,
        name: m.title,
        status: m.status === '1' ? 'Aktif' : 'Pasif',
        city: m.city,
        county: m.county,
      })),
    [malls]
  );

  // 👇 AVM adına göre filtrelenmiş satırlar
  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r) => r.name?.toLowerCase().includes(term));
  }, [rows, search]);

  const handleCheckboxChange = (_e, rowId) => {
    setSelected((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // sadece ekranda görünen (filtrelenmiş) satırları seç
      setSelected(filteredRows.map((r) => r.id));
    } else {
      setSelected([]);
    }
  };

  const handleViewPriceList = (row) => {
    setSelectedMallName(row.name);
    setSelectedMallID(row.id);
    setOpen(true);
  };

  const handleEdit = (row) => {
    setSelectedMallName(row.name);
    setSelectedMallID(row.id);
    setOpenEdit(true);
  };

  const handleEditSubmit = (payload) => {
    // TODO: Services.UpdateMallPrices(...)
    console.log('Güncelle payload:', payload);
  };

  const headers = useMemo(
    () => [
      { id: 'checkbox', label: '', sx: { width: 56, px: 1 } },
      { id: 'no', label: 'NO', align: 'left', sx: { width: 72 } },
      {
        id: 'name',
        label: 'AVM / PLAZA ADI',
        sortBy: 'name',
        align: 'left',
        sx: { minWidth: 240 },
      },
      {
        id: 'status',
        label: 'NOKTA DURUMU',
        sortFunction: (row) => (row.status === 'Aktif' ? 1 : 0),
        align: 'left',
        sx: { width: 160 },
        format: (value) => (
          <Chip
            label={value}
            size="small"
            variant="outlined"
            sx={{
              borderColor: value === 'Aktif' ? '#BBF7D0' : '#E5E7EB',
              color: value === 'Aktif' ? '#15803D' : '#6B7280',
              backgroundColor: value === 'Aktif' ? '#ECFDF5' : '#F3F4F6',
              fontWeight: 600,
              px: 1,
            }}
          />
        ),
      },
      {
        id: 'priceList',
        label: 'FİYAT LİSTESİ',
        align: 'left',
        sx: { width: 180 },
        format: (_value, row) => (
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              handleViewPriceList(row);
            }}
            sx={{
              textTransform: 'none',
              borderColor: '#93C5FD',
              color: '#2563EB',
              borderRadius: '24px',
              backgroundColor: '#EFF6FF',
              fontWeight: 500,
              '&:hover': { backgroundColor: '#E0F2FE' },
            }}
          >
            Listeyi görüntüle
          </Button>
        ),
      },
      {
        id: 'edit',
        label: 'DÜZENLE',
        align: 'center',
        sx: { width: 96 },
        format: (_value, row) => (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(row);
            }}
            aria-label="Düzenle"
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    []
  );

  const locationOptions = useMemo(
    () => malls.map((m) => ({ id: m.id, title: m.title })),
    [malls]
  );

  const handleCreateCoupon = (_resOrPayload) => {
    // fetchMalls();
    // setOpenCoupon(false);
  };

  return (
    <>
      <Helmet>
        <title>EP Hizmet Yönetimi</title>
      </Helmet>

      <Stack sx={{ px: 3, py: 3 }}>
        <Stack direction="row" justifyContent="space-between" mb={3} gap={2} flexWrap="wrap">
          <Typography variant="h4">AVM / PLAZA BİLGİ DÜZENLEME</Typography>

          <Stack direction="row" spacing={2} alignItems="center">
            {/* 👇 AVM adına göre arama inputu */}
            <TextField
              size="small"
              variant="outlined"
              placeholder="AVM adına göre ara"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 260,backgroundColor:'#fff' }}
            />

            <AddEntityButton
              onClick={() => setOpenCoupon(true)}
              label={'Kupon Kodu Üret'}
              icon={'mingcute:tag-2-line'}
              disabled={!malls.length}
            />
          </Stack>
        </Stack>

        <Card variant="outlined" sx={{ p: 0, overflow: 'hidden' }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" py={6}>
              <CircularProgress size={32} />
            </Stack>
          ) : (
            <DynamicTable
              data={filteredRows}                // 👈 filtrelenmiş data
              headers={headers}
              rowsPerPageOptions={[10, 25, 50]}
              initialRowsPerPage={10}
              initialOrder="name"
              initalOrderDirection="asc"
              isOrdered={false}
              selected={selected}
              handleCheckboxChange={handleCheckboxChange}
              handleSelectAll={handleSelectAll}
              isMobile={isMobile}
              rowStyles={() => ({ '& td': { py: 1.5 } })}
            />
          )}
        </Card>
      </Stack>

      <MallPriceListDialog
        open={open}
        onClose={() => setOpen(false)}
        mallID={selectedMallID}
        mallName={selectedMallName}
      />

      <MallPriceEditDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        mallID={selectedMallID}
        mallName={selectedMallName}
        onSubmit={handleEditSubmit}
      />

      <CouponCreateDialog
        open={openCoupon}
        onClose={() => setOpenCoupon(false)}
        onSubmit={handleCreateCoupon}
        locations={locationOptions}
      />
    </>
  );
}